import { z } from 'zod'

import {
  AnnouncementLogActionChoice,
  type AnnouncementMediaRequest,
  AnnouncementPlacementChoice,
  type AnnouncementUpdateRequest,
} from '@gto/api-client/admin/react-query'
import { listAnnouncementLogsQuerySearchMaxOne } from '@gto/api-client/admin/zod'

import { trimmedTextParam } from '@/utils/search-params'

// Every field the update endpoint accepts. A `satisfies Record` rather than a
// plain list, because a field added to the API must also become filterable
// here: with a list, the new field compiles and simply never appears in the
// dropdown. The change reason is excluded on purpose — it is never stored on the
// announcement, so no diff can touch it.
const UPDATE_FIELDS = {
  name: true,
  tag: true,
  placement: true,
  title: true,
  title_highlight: true,
  title_size_px: true,
  body: true,
  body_style: true,
  countdown_target: true,
  countdown_label: true,
  countdown_expiry: true,
  blocks: true,
  modal_background: true,
  modal_width: true,
  toaster_media_layout: true,
  toaster_show_new_label: true,
  cta_text: true,
  cta_link: true,
  cta_target: true,
  secondary_cta_text: true,
  secondary_cta_link: true,
  route_prefixes: true,
  route_exclude_prefixes: true,
  audience: true,
  audience_tiers: true,
  audience_plans: true,
  audience_inverted: true,
  insider_gate: true,
  min_account_age_days: true,
  max_account_age_days: true,
  required_flag: true,
  required_flag_inverted: true,
  locales: true,
  locales_inverted: true,
  dismissible: true,
  start_date: true,
  end_date: true,
  auto_close_seconds: true,
  min_hours_since_previous: true,
  is_paused: true,
  is_preview: true,
  priority: true,
} as const satisfies Record<Exclude<keyof AnnouncementUpdateRequest, 'change_reason'>, true>

// The media endpoint writes these, so its rows carry them as field names even
// though no update body does. Keyed by the request's upload field, because the
// stored field name differs from it: a new upload field on the API then fails to
// compile here until it names the field its log rows carry.
const MEDIA_FIELDS = {
  image_upload_id: 'image',
  video_upload_id: 'video',
  background_upload_id: 'modal_background_image',
  background_mobile_upload_id: 'modal_background_image_mobile',
} as const satisfies Record<Extract<keyof AnnouncementMediaRequest, `${string}_upload_id`>, string>

/**
 * A field name a log row can carry. A type, so the change log's label map must
 * name every one of them: a new API field fails to compile there until it gets
 * a label.
 */
export type LogField = keyof typeof UPDATE_FIELDS | (typeof MEDIA_FIELDS)[keyof typeof MEDIA_FIELDS]

/** The changed-field filter's values, in alphabetical order. */
export const LOG_FIELDS: readonly string[] = [
  ...Object.keys(UPDATE_FIELDS),
  ...Object.values(MEDIA_FIELDS),
].sort((left, right) => left.localeCompare(right))

/** The free-text search cap. Read from the generated client, so it follows the API's own limit. */
export const LOG_SEARCH_MAX_LENGTH = listAnnouncementLogsQuerySearchMaxOne

// An id the API could never match is DROPPED rather than forwarded: a stale or
// hand-edited link then degrades to the wider log the reader can narrow again,
// where forwarding it would turn the link into a 422 error page.
const idSchema = z.coerce.number().int().positive().optional().catch(undefined)

// `YYYY-MM-DD` and a real calendar day: `2026-02-31` is dropped, as the API
// refuses it.
const dateSchema = z.iso.date().optional().catch(undefined)

/**
 * The change log keeps its view state in the URL, so a staff member can hand a
 * colleague the exact history they are reading — and so the "History" link on
 * an announcement is just this page with `announcement_id` set.
 *
 * Each filter key has the name of the endpoint's own parameter, so the page
 * forwards the filters as they are, without a translation table. `page` is the
 * one exception: it is 1-based, what the address bar shows, and the page
 * converts it to the API's `offset`. Lives beside the page so the route
 * declaration and its tests read one definition.
 */
export const announcementLogsSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().catch(undefined),
  announcement_id: idSchema,
  actor_id: idSchema,
  action: z.enum(AnnouncementLogActionChoice).optional().catch(undefined),
  placement: z.enum(AnnouncementPlacementChoice).optional().catch(undefined),
  date_from: dateSchema,
  date_to: dateSchema,
  field: z
    .string()
    .refine((value) => LOG_FIELDS.includes(value))
    .optional()
    .catch(undefined),
  search: trimmedTextParam(LOG_SEARCH_MAX_LENGTH),
})

export type AnnouncementLogsSearch = z.infer<typeof announcementLogsSearchSchema>

/** The filter keys of the URL. `page` is the page's own concern. */
export type AnnouncementLogFilterValues = Omit<AnnouncementLogsSearch, 'page'>

/**
 * Whether the reader has narrowed the log at all. Shared because two readers
 * ask it: the filter row, which offers "Clear all filters", and the empty
 * state, which must say "nothing matches" rather than "nothing changed yet".
 */
export const hasAnyLogFilter = (filters: AnnouncementLogFilterValues): boolean =>
  // `<unknown>` because the type-aware lint (`no-unnecessary-condition`)
  // rejects the plain form. A filter the schema dropped is `undefined`.
  Object.values<unknown>(filters).some((value) => value !== undefined)
